import { groupBy, omit } from 'lodash'
import {
  CLIENT_ONLY_DEVICE_FIELDS,
  DEVICES_API,
  DUPLEX_LONG_EDGE,
  PACKING_SLIP_FORMAT,
  PRINTER_FIELDS,
  PRINT_SERVER_SETTING,
  RETURN_POLICY_CLIENTS,
} from '../constants/Constants'
import { isNonEmptyArray } from './helpers'

export const [LETTER_PRINTER_FIELD, LABEL_PRINTER_FIELD] = PRINTER_FIELDS

// 4x6 clients print their slip on the label printer
export const getPackingSlipPrinterField = (client) =>
  +client?.packing_slip_format === PACKING_SLIP_FORMAT.LABEL_4X6
    ? LABEL_PRINTER_FIELD
    : LETTER_PRINTER_FIELD

// each letter slip page is followed by the policy page meant for its back
export const getPackingSlipPrintOptions = (order, printerField) =>
  printerField === LETTER_PRINTER_FIELD && RETURN_POLICY_CLIENTS.includes(+order?.client_id)
    ? { duplex: DUPLEX_LONG_EDGE }
    : {}

export const normalizeHost = (host) => (host || DEVICES_API).replace(/\/+$/, '')

export const isLocalHost = (host) => normalizeHost(host) === normalizeHost(DEVICES_API)

export const hasSelectedPrinter = (printers, devices, field) =>
  !!printers?.some(
    (printer) =>
      printer.name === devices?.[field.name] &&
      normalizeHost(printer.host) === normalizeHost(devices?.[field.host]),
  )

export const getHostLabel = (host) => {
  try {
    return new URL(normalizeHost(host)).host
  } catch {
    return normalizeHost(host)
  }
}

export const getPrintServerConfigs = (settings) => {
  if (!isNonEmptyArray(settings)) return []

  return settings.reduce((configs, setting) => {
    if (setting?.key !== PRINT_SERVER_SETTING.KEY || setting?.code !== PRINT_SERVER_SETTING.CODE) {
      return configs
    }
    let value
    try {
      value = JSON.parse(setting?.value ?? '{}')
    } catch {
      return configs
    }

    const path = value?.[PRINT_SERVER_SETTING.VALUE_FIELD]
    if (!path || isLocalHost(path)) return configs

    const host = normalizeHost(path)
    const printers = Array.isArray(value?.printers) ? value.printers : []
    const existingConfig = configs.find((config) => config.host === host)

    if (existingConfig) {
      existingConfig.printers.push(...printers)
      return configs
    }

    return [...configs, { host, printers }]
  }, [])
}

export const getPrintServerHosts = (settings) =>
  getPrintServerConfigs(settings).map((config) => config.host)

export const filterPrintServerPrinters = (
  printers,
  printServer,
  warehouseId,
  canAccessAllWarehouses,
) => {
  if (canAccessAllWarehouses) return printers
  if (!warehouseId) return []

  return printers.filter((printer) => {
    const printerAccess = printServer?.printers?.find(
      (configuredPrinter) => configuredPrinter?.name === printer.name,
    )

    return printerAccess?.warehouse_ids?.some(
      (allowedWarehouseId) => Number(allowedWarehouseId) === Number(warehouseId),
    )
  })
}

export const groupPrintersByHost = (printers, t) => {
  const groups = groupBy(isNonEmptyArray(printers) ? printers : [], (printer) =>
    normalizeHost(printer.host),
  )
  return Object.entries(groups).map(([host, hostPrinters]) => ({
    host,
    label: isLocalHost(host) ? t('local-print-service') : t('network-print-service'),
    printers: hostPrinters,
  }))
}

export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result.split('base64,')[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

export const toDeviceSettingsPayload = (devices, host) => {
  const foreignPrinters = PRINTER_FIELDS.filter(
    (field) => normalizeHost(devices?.[field.host]) !== normalizeHost(host),
  ).map((field) => field.name)

  return omit(devices, [...CLIENT_ONLY_DEVICE_FIELDS, ...foreignPrinters])
}
